import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import type {
  ComplianceDocumentKind,
  ComplianceDocumentManifest,
  InstructionHandbookSectionConfig,
  SectionConfig,
} from '@artem-lex/compliance-documents'
import { formatComplianceDocumentPlaceholderMessage } from '@artem-lex/compliance-documents'
import { apiClient } from '@/shared/api/apiClient'

type PlaceholderMessageType = 'not_found'

type ComplianceDocumentManifestContextValue = {
  manifest: ComplianceDocumentManifest | undefined
  isLoading: boolean
  documentKind: ComplianceDocumentKind
  sectionOptions: ReadonlyArray<{ id: string }>
  formatSectionPickerLabel: (sectionId: string) => string
  resolveSectionDisplayTitle: (section: {
    sectionId: string
    title: string
    kind: 'canonical' | 'custom'
  }) => string
  formatPlaceholderMessage: (
    type: PlaceholderMessageType,
    fieldLabel?: string | null,
    guidance?: string | null
  ) => string
  getDismissAriaLabel: () => string
  getInsufficientInformationMessage: () => string
}

const ComplianceDocumentManifestContext =
  createContext<ComplianceDocumentManifestContextValue | null>(null)

const COMPLIANCE_DOCUMENT_MANIFEST_QUERY_KEY = 'complianceDocumentManifest'

function getSectionTitleFromManifest(
  manifest: ComplianceDocumentManifest,
  sectionId: string
): string | null {
  for (const section of manifest.sections) {
    const config = section as SectionConfig | InstructionHandbookSectionConfig
    if (config.section === sectionId) {
      return config.title
    }
  }
  return null
}

function formatRiskAssessmentAdminSectionTitle(
  manifest: ComplianceDocumentManifest,
  sectionId: string
): string {
  const title = getSectionTitleFromManifest(manifest, sectionId) ?? sectionId
  if (sectionId.includes('.') && sectionId.split('.').length > 2) {
    return `${sectionId} ${title}`
  }
  return title
}

function formatRiskAssessmentSectionPickerLabel(
  manifest: ComplianceDocumentManifest,
  sectionId: string
): string {
  const adminTitle = formatRiskAssessmentAdminSectionTitle(manifest, sectionId)
  const idWithSpace = `${sectionId} `
  if (adminTitle.startsWith(idWithSpace)) {
    return `${sectionId}. ${adminTitle.slice(idWithSpace.length).trim()}`
  }
  return `${sectionId}. ${adminTitle}`
}

const FALLBACK_SENTINEL_DISPLAY = {
  notFound: 'Not found',
  notFoundWithField: 'Not found: {{field}}',
  notFoundWithFieldAndGuidance: 'Not found: {{field}} | {{guidance}}',
  dismissAriaLabel: 'Dismiss notice',
  insufficientInformation:
    'Insufficient information: No relevant content was found in the product sources for this section. Please add source documents or complete manually.',
}

function useComplianceDocumentManifestQuery(
  documentKind: ComplianceDocumentKind | undefined,
  locale: string | undefined,
  manifestBasePath: string
) {
  return useQuery<ComplianceDocumentManifest, Error>({
    queryKey: [
      COMPLIANCE_DOCUMENT_MANIFEST_QUERY_KEY,
      manifestBasePath,
      documentKind,
      locale,
    ],
    queryFn: async () => {
      if (!documentKind) {
        throw new Error('documentKind is required')
      }
      const params = new URLSearchParams({ documentKind })
      if (locale) {
        params.set('locale', locale)
      }
      const response = await apiClient.get<ComplianceDocumentManifest>(
        `${manifestBasePath}?${params.toString()}`
      )
      return response.data
    },
    enabled: documentKind != null,
    staleTime: 24 * 60 * 60 * 1000,
  })
}

type ComplianceDocumentManifestProviderProps = {
  documentKind: ComplianceDocumentKind
  locale: string
  manifestBasePath?: string
  children: ReactNode
}

export function ComplianceDocumentManifestProvider({
  documentKind,
  locale,
  manifestBasePath = '/machinery-directive-compliance/document-manifest',
  children,
}: ComplianceDocumentManifestProviderProps) {
  const { data: manifest, isLoading } = useComplianceDocumentManifestQuery(
    documentKind,
    locale,
    manifestBasePath
  )

  const sectionOptions = useMemo(() => {
    if (!manifest) {
      return []
    }
    return manifest.sections.map((section) => {
      const config = section as SectionConfig | InstructionHandbookSectionConfig
      return { id: config.section }
    })
  }, [manifest])

  const formatSectionPickerLabel = useCallback(
    (sectionId: string) => {
      if (!manifest) {
        return sectionId
      }
      if (documentKind === 'risk_assessment') {
        return formatRiskAssessmentSectionPickerLabel(manifest, sectionId)
      }
      const title = getSectionTitleFromManifest(manifest, sectionId) ?? sectionId
      return `${sectionId}. ${title}`
    },
    [documentKind, manifest]
  )

  const resolveSectionDisplayTitle = useCallback(
    (section: {
      sectionId: string
      title: string
      kind: 'canonical' | 'custom'
    }) => {
      const storedTitle = section.title.trim()
      if (storedTitle.length > 0) {
        return storedTitle
      }
      if (section.kind === 'canonical' && manifest) {
        if (documentKind === 'risk_assessment') {
          const label = formatRiskAssessmentAdminSectionTitle(
            manifest,
            section.sectionId
          )
          const idPrefix = `${section.sectionId} `
          if (label.startsWith(idPrefix)) {
            return label.slice(idPrefix.length).trim()
          }
          return label
        }
        const label =
          getSectionTitleFromManifest(manifest, section.sectionId) ??
          section.sectionId
        const idPrefix = `${section.sectionId} `
        if (label.startsWith(idPrefix)) {
          return label.slice(idPrefix.length).trim()
        }
        return label
      }
      return section.sectionId
    },
    [documentKind, manifest]
  )

  const formatPlaceholderMessage = useCallback(
    (
      type: PlaceholderMessageType,
      fieldLabel?: string | null,
      guidance?: string | null
    ) => {
      const display =
        manifest?.sentinels.display ?? FALLBACK_SENTINEL_DISPLAY
      return formatComplianceDocumentPlaceholderMessage(
        type,
        display,
        fieldLabel,
        guidance
      )
    },
    [manifest]
  )

  const getDismissAriaLabel = useCallback(() => {
    return (
      manifest?.sentinels.display.dismissAriaLabel ??
      FALLBACK_SENTINEL_DISPLAY.dismissAriaLabel
    )
  }, [manifest])

  const getInsufficientInformationMessage = useCallback(() => {
    return (
      manifest?.sentinels.display.insufficientInformation ??
      FALLBACK_SENTINEL_DISPLAY.insufficientInformation
    )
  }, [manifest])

  const value = useMemo(
    (): ComplianceDocumentManifestContextValue => ({
      manifest,
      isLoading,
      documentKind,
      sectionOptions,
      formatSectionPickerLabel,
      resolveSectionDisplayTitle,
      formatPlaceholderMessage,
      getDismissAriaLabel,
      getInsufficientInformationMessage,
    }),
    [
      manifest,
      isLoading,
      documentKind,
      sectionOptions,
      formatSectionPickerLabel,
      resolveSectionDisplayTitle,
      formatPlaceholderMessage,
      getDismissAriaLabel,
      getInsufficientInformationMessage,
    ]
  )

  return (
    <ComplianceDocumentManifestContext.Provider value={value}>
      {children}
    </ComplianceDocumentManifestContext.Provider>
  )
}

export function useComplianceDocumentManifestContext(): ComplianceDocumentManifestContextValue {
  const context = useContext(ComplianceDocumentManifestContext)
  if (!context) {
    throw new Error(
      'useComplianceDocumentManifestContext must be used within ComplianceDocumentManifestProvider'
    )
  }
  return context
}

export function useComplianceDocumentManifestContextOptional():
  ComplianceDocumentManifestContextValue | null {
  return useContext(ComplianceDocumentManifestContext)
}
